import { useState } from 'preact/hooks'
import type { JSX } from 'preact'
import { formatSessionClock } from '../time'
import { seekTargetAt, seekTargetForKey } from './positions'

interface SeekSliderOptions {
    label: string
    /** Where the player is now. */
    timeSeconds: number
    durationSeconds: number
    onSeek: (seconds: number) => void
}

export interface SeekSlider {
    /** The moment the bar shows: under the pointer while it drags, otherwise the player's. */
    shownSeconds: number
    props: JSX.HTMLAttributes<HTMLDivElement>
}

const pointerSeconds = (event: JSX.TargetedPointerEvent<HTMLDivElement>, durationSeconds: number) => {
    const bar = event.currentTarget.getBoundingClientRect()
    return seekTargetAt(event.clientX, bar.left, bar.width, durationSeconds)
}

/**
 * A seek bar any skin can draw: a `role=slider` that the arrow keys, Home and End move, and that a pointer drags.
 * Dragging only moves the bar; the player seeks once, where the pointer lets go, rather than on every move.
 */
export function useSeekSlider({ label, timeSeconds, durationSeconds, onSeek }: SeekSliderOptions): SeekSlider {
    const [dragSeconds, setDragSeconds] = useState<number | null>(null)
    const shownSeconds = dragSeconds ?? timeSeconds
    const usable = durationSeconds > 0

    return {
        shownSeconds,
        props: {
            role: 'slider',
            tabIndex: usable ? 0 : -1,
            'aria-label': label,
            'aria-valuemin': 0,
            'aria-valuemax': Math.floor(durationSeconds),
            'aria-valuenow': Math.floor(shownSeconds),
            'aria-valuetext': formatSessionClock(shownSeconds),
            'aria-disabled': usable ? undefined : true,
            onKeyDown: event => {
                if (!usable) return
                const target = seekTargetForKey(event.key, timeSeconds, durationSeconds)
                if (target === null) return
                event.preventDefault()
                onSeek(target)
            },
            onPointerDown: event => {
                if (!usable || event.button !== 0) return
                event.currentTarget.setPointerCapture?.(event.pointerId)
                setDragSeconds(pointerSeconds(event, durationSeconds))
            },
            onPointerMove: event => {
                if (dragSeconds !== null) setDragSeconds(pointerSeconds(event, durationSeconds))
            },
            onPointerUp: event => {
                if (dragSeconds === null) return
                onSeek(pointerSeconds(event, durationSeconds))
                setDragSeconds(null)
            },
            onPointerCancel: () => setDragSeconds(null),
        },
    }
}
